"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  UserDataPushNotificationConfig,
  UserDataPushNotificationContextType,
} from "./types";

const UserDataPushNotificationContext = createContext<
  UserDataPushNotificationContextType | undefined
>(undefined);

export const UserDataPushNotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [current, setCurrent] = useState<UserDataPushNotificationConfig | null>(
    null,
  );
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef(false);
  const timerExpiredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const closeUserDataPushNotification = useCallback(() => {
    clearTimer();
    setCurrent(null);
    timerExpiredRef.current = false;
  }, [clearTimer]);

  const setIsHovering = useCallback(
    (hovering: boolean) => {
      isHoveringRef.current = hovering;
      if (!hovering && timerExpiredRef.current) {
        closeUserDataPushNotification();
      }
    },
    [closeUserDataPushNotification],
  );

  const showUserDataPushNotification = useCallback(
    (config: UserDataPushNotificationConfig) => {
      const withDefaults: UserDataPushNotificationConfig = {
        ...config,
        id: config.id || `userdata-push-${Date.now()}`,
        viewMoreHref: config.viewMoreHref ?? "#",
        autoClose: config.autoClose !== false,
        duration: config.duration ?? 10000,
      };
      setCurrent(withDefaults);
    },
    [],
  );

  useEffect(() => {
    if (current && current.autoClose !== false) {
      clearTimer();
      timerExpiredRef.current = false;
      const duration = current.duration ?? 10000;
      timerRef.current = setTimeout(() => {
        timerExpiredRef.current = true;
        if (!isHoveringRef.current) {
          closeUserDataPushNotification();
        }
      }, duration);
    }
    return () => {
      clearTimer();
      timerExpiredRef.current = false;
    };
  }, [current, clearTimer, closeUserDataPushNotification]);

  const value: UserDataPushNotificationContextType = {
    showUserDataPushNotification,
    closeUserDataPushNotification,
    current,
    setIsHovering,
  };

  return (
    <UserDataPushNotificationContext.Provider value={value}>
      {children}
    </UserDataPushNotificationContext.Provider>
  );
};

export const useUserDataPushNotificationContext =
  (): UserDataPushNotificationContextType => {
    const ctx = useContext(UserDataPushNotificationContext);
    if (ctx === undefined) {
      throw new Error(
        "useUserDataPushNotificationContext must be used within UserDataPushNotificationProvider",
      );
    }
    return ctx;
  };
